import { useNavigate, useParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { ArrowLeft, Loader2, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiHttpError, requestResetCode } from '@/api/auth'
import { defaultLocale } from '@/lib/locales'

const schema = z.object({
  email: z.string().min(1, 'Email requis').email('Email invalide'),
})

type Form = z.infer<typeof schema>

import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function ForgotPasswordPage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  useDocumentTitle(t('forgotPassword.pageTitle', 'Mot de passe oublié'))

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({ resolver: zodResolver(schema), defaultValues: { email: '' } })

  const mutation = useMutation({
    mutationFn: async (values: Form) => {
      await requestResetCode(values.email, locale)
      return values.email.trim().toLowerCase()
    },
    onSuccess: (normalizedEmail) => {
      navigate(
        `/${locale}/reset-password?email=${encodeURIComponent(normalizedEmail)}`,
        { replace: true },
      )
    },
    onError: (error) => {
      if (error instanceof ApiHttpError) {
        if (error.status === 429) {
          toast.error(
            t('auth.forgotPassword.tooManyAttempts') ||
              'Trop de tentatives. Veuillez patienter une minute.',
          )
          return
        }
        const data = error.data as { error?: string; detail?: string }
        toast.error(
          data?.error || data?.detail || t('auth.forgotPassword.errorOccurred', 'Erreur'),
        )
        return
      }
      console.error('[ForgotPassword]', error)
      toast.error(t('auth.forgotPassword.connectionError', 'Erreur de connexion'))
    },
  })

  const onSubmit = handleSubmit((values) => mutation.mutate(values))
  const isSubmitting = mutation.isPending

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white text-slate-900">
      <div className="relative hidden md:flex items-end bg-[#0A192F] text-white p-10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(800px 400px at 20% 10%, #ffffff 0%, transparent 60%)',
          }}
        />
        <div className="relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-white" />{' '}
            {t('auth.forgotPassword.badge', 'Récupération sécurisée')}
          </div>
          <h1 className="text-3xl font-semibold leading-tight">
            {t('auth.forgotPassword.leftTitle', 'Réinitialisez votre mot de passe')}
          </h1>
          <p className="text-white/80 max-w-md">
            {t('auth.forgotPassword.leftSubtitle') ||
              'Entrez votre adresse email et nous vous enverrons un code de vérification pour créer un nouveau mot de passe.'}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-6 flex items-center gap-2">
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              className="h-7 w-7"
            />
            <span className="font-semibold">Callizy</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              {t('auth.forgotPassword.title')}
            </h2>
            <p className="text-slate-600 text-sm">{t('auth.forgotPassword.description')}</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="email" className="text-slate-700">
                {t('auth.forgotPassword.emailLabel')}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
                autoComplete="email"
                aria-invalid={!!errors.email}
                disabled={isSubmitting}
                className="mt-1"
                {...register('email')}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 bg-[#0A192F] text-white py-2.5 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('auth.forgotPassword.sending')}
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  {t('auth.forgotPassword.sendButton')}
                </>
              )}
            </Button>

            <div className="text-center pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => navigate(`/${locale}/login`)}
                className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t('auth.forgotPassword.backToLogin')}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}
